import { SelectOption, SelectPropsValue } from 'commons/components';
import React from 'react';
import { OnChangeAutomationRuleStateData, RuleActionDataMap, RuleCondition } from '../types/automation-rules.type';

export function injectPropsToMatchingChildren(
    children: React.ReactNode,
    injections: Record<string, Record<string, unknown>>
): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }

        const type = child.type;

        let typeName: string | undefined;

        if (typeof type === 'string') {
            typeName = type;
        } else if (typeof type === 'function' || typeof type === 'object') {
            typeName =
                (type as { displayName?: string; name?: string }).displayName ?? (type as { name?: string }).name;
        }

        const newProps = typeName ? injections[typeName] : undefined;

        return React.cloneElement(
            child,
            newProps ? { ...child.props, ...newProps } : child.props,
            injectPropsToMatchingChildren(child.props.children, injections)
        );
    });
}

interface AutomationRuleState {
    state: {
        [state: string]: unknown;
    };
}

type HandlerFunction = (data: OnChangeAutomationRuleStateData & AutomationRuleState) => void;

const onChangeTypeHandlers: Record<string, HandlerFunction> = {
    input: ({ key, value, state }) => {
        if (key) {
            state[key] = value;
        }
    },

    keep_value: ({ key, value, state, parentArrayName, index, customIdentifier }) => {
        if (!parentArrayName || key === undefined || index === undefined) {
            return;
        }

        const array = state[parentArrayName];
        if (!Array.isArray(array)) {
            return;
        }

        array[index] = {
            ...array[index],
            [key]: value,
            ...(customIdentifier ? { customIdentifier } : {}),
        };
    },

    delete_array_value: ({ state, parentArrayName, index }) => {
        if (parentArrayName && index !== undefined) {
            const array = state[parentArrayName];
            if (Array.isArray(array)) {
                array[index] = null;
            }
        }
    },

    add_array_value: ({ state, parentArrayName, index }) => {
        if (parentArrayName && index !== undefined) {
            const array = state[parentArrayName];
            if (Array.isArray(array)) {
                array[index] = undefined;
            }
        }
    },
};

export function onChange(data: OnChangeAutomationRuleStateData & AutomationRuleState) {
    if (!data || !data.type) {
        return;
    }

    const handler = onChangeTypeHandlers[data.type];
    if (handler) {
        handler(data);
    }
}

type ParentChildPair = { parentValue: string; childValue: string };

export function findParentsAndChildrenByKey(data: unknown, childValues: string[], valueKey: string): ParentChildPair[] {
    if (!Array.isArray(data)) {
        return [];
    }

    return data.flatMap((parent) => processParent(parent, childValues, valueKey)).filter(Boolean) as ParentChildPair[];
}

function processParent(parent: unknown, childValues: string[], valueKey: string): (ParentChildPair | null)[] {
    if (
        typeof parent !== 'object' ||
        parent === null ||
        !('children' in parent) ||
        !Array.isArray((parent as Record<string, unknown>).children)
    ) {
        return [];
    }

    const parentValue = (parent as Record<string, unknown>)[valueKey];
    const children = (parent as Record<string, unknown>).children as unknown[];

    return children.map((child) => processChild(child, parentValue, childValues, valueKey));
}

function processChild(
    child: unknown,
    parentValue: unknown,
    childValues: string[],
    valueKey: string
): ParentChildPair | null {
    if (
        typeof child === 'object' &&
        child !== null &&
        valueKey in child &&
        typeof (child as Record<string, unknown>)[valueKey] === 'string'
    ) {
        const childValue = (child as Record<string, unknown>)[valueKey] as string;
        if (childValues.includes(childValue)) {
            return {
                parentValue: String(parentValue),
                childValue,
            };
        }
    }
    return null;
}
/**
 * What the three validators below actually read. Declared structurally so a draft being edited —
 * which has no id/version yet and whose action type may still be empty — validates with the same
 * code as a persisted Rule.
 */
type ValidatableAction = { type?: string; data?: Partial<RuleActionDataMap[keyof RuleActionDataMap]> };
type ValidatableCondition = { field?: string; value?: RuleCondition['value'] };
export type ValidatableRule = {
    name?: string;
    trigger?: string;
    actions?: ValidatableAction[];
    conditions?: ValidatableCondition[];
};

function isNonEmptyString(value?: string): boolean {
    return typeof value === 'string' && value.trim() !== '';
}

function isValidAction(action: ValidatableAction): boolean {
    if (action === undefined) {
        return false;
    }
    if (action === null) {
        return true;
    } // nulls are allowed
    if (!isNonEmptyString(action.type)) {
        return false;
    }
    if (
        !action.data ||
        typeof action.data !== 'object' ||
        Array.isArray(action.data) ||
        Object.keys(action.data).length === 0
    ) {
        return false;
    }
    return true;
}

function isValidCondition(condition: ValidatableCondition): boolean {
    if (condition === undefined) {
        return false;
    }
    if (condition === null) {
        return true;
    } // nulls are allowed
    if (!isNonEmptyString(condition.field)) {
        return false;
    }
    if (condition.value === undefined || condition.value === null || condition.value === '') {
        return false;
    }
    return true;
}

export function isRuleObjectValid(rule: ValidatableRule): boolean {
    if (!isNonEmptyString(rule?.name)) {
        return false;
    }
    if (!isNonEmptyString(rule?.trigger)) {
        return false;
    }

    if (!Array.isArray(rule.actions) || rule.actions.length === 0) {
        return false;
    }
    if (!rule.actions.every(isValidAction)) {
        return false;
    }

    if (!Array.isArray(rule.conditions)) {
        return false;
    }
    if (!rule.conditions.every(isValidCondition)) {
        return false;
    }

    return true;
}

type Child = {
    value: string | number;
    text: string;
};

interface GetParentNameByChildValue {
    data: SelectOption[];
    childValue: SelectPropsValue;
}

export function getParentNameByChildValue({ data = [], childValue }: GetParentNameByChildValue): string | null {
    if (!Array.isArray(data) || data.length === 0) {
        return null;
    }

    for (const parent of data) {
        if (parent.children?.some((child: Child) => child.value === childValue)) {
            return parent.text;
        }
    }
    return null;
}
